import React, { useMemo, useState } from "react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
  Tooltip,
  TooltipProps,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import { Label } from "~/components/ui/label";
import {
  buildIndexComparisonData,
  getTestColor,
  TestWithResults,
  IndexComparisonDataPoint,
} from "./utils";

interface ComparisonRadarChartProps {
  tests: TestWithResults[];
}

interface CustomTickProps {
  x?: number;
  y?: number;
  payload?: { value: string };
  textAnchor?: string;
}

const CustomAngleTick: React.FC<CustomTickProps> = ({
  x,
  y,
  payload,
  textAnchor,
}) => {
  return (
    <text
      x={x}
      y={y}
      textAnchor={textAnchor}
      fontSize={10}
      className="fill-muted-foreground"
    >
      {payload?.value}
    </text>
  );
};

const CustomRadiusTick: React.FC<CustomTickProps> = ({ x, y, payload }) => {
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      fontSize={10}
      className="fill-muted-foreground"
    >
      {payload?.value}
    </text>
  );
};

const CustomTooltip: React.FC<
  TooltipProps<number, string> & { tests: TestWithResults[] }
> = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const dataPoint = payload[0]?.payload as IndexComparisonDataPoint;

  return (
    <div className="rounded-lg border bg-background p-3 shadow-lg">
      <p className="mb-2 font-semibold text-foreground">
        {dataPoint?.displayName || label}
      </p>
      <div className="space-y-1.5">
        {payload.map((entry) => {
          if (entry.value === undefined) {
            return null;
          }

          const testId = entry.dataKey as string;
          const rawValue = dataPoint?.[`${testId}_raw`] as number | undefined;
          const indexRating = entry.value;

          return (
            <div key={testId} className="flex items-center gap-2">
              <div
                className="h-3 w-3 rounded-sm"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-sm text-muted-foreground">
                {entry.name}:
              </span>
              <span className="text-sm font-medium text-foreground">
                Index {typeof indexRating === "number" ? indexRating : "N/A"}
              </span>
              <span className="text-xs text-muted-foreground">
                ({rawValue !== undefined ? rawValue.toFixed(2) : "N/A"}{" "}
                {dataPoint?.unit})
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ComparisonRadarChart: React.FC<ComparisonRadarChartProps> = ({
  tests,
}) => {
  const [visibleTests, setVisibleTests] = useState<Set<string>>(
    new Set(tests.map((t) => t.id))
  );

  const chartData = useMemo(() => buildIndexComparisonData(tests), [tests]);

  const toggleTest = (testId: string) => {
    setVisibleTests((prev) => {
      const next = new Set(prev);
      if (next.has(testId)) {
        next.delete(testId);
      } else {
        next.add(testId);
      }
      return next;
    });
  };

  if (chartData.length === 0) {
    return (
      <Card>
        <CardContent className="flex h-64 items-center justify-center">
          <p className="text-muted-foreground">
            No comparable indicators found in the selected tests.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Indicator Profile</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex flex-wrap gap-4">
          {tests.map((test, index) => (
            <div key={test.id} className="flex items-center gap-2">
              <Checkbox
                id={`test-toggle-${test.id}`}
                checked={visibleTests.has(test.id)}
                onCheckedChange={() => toggleTest(test.id)}
              />
              <Label
                htmlFor={`test-toggle-${test.id}`}
                className="flex cursor-pointer items-center gap-2 text-sm"
              >
                <div
                  className="h-3 w-3 rounded-sm"
                  style={{ backgroundColor: getTestColor(index) }}
                />
                {test.name}
              </Label>
            </div>
          ))}
        </div>

        <div className="h-[500px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart
              cx="50%"
              cy="50%"
              outerRadius="70%"
              data={chartData}
              margin={{ top: 20, right: 30, bottom: 20, left: 30 }}
            >
              <PolarGrid className="stroke-border" />
              <PolarAngleAxis
                dataKey="displayName"
                tick={<CustomAngleTick />}
                tickLine={false}
              />
              <PolarRadiusAxis
                angle={90}
                domain={[0, 5]}
                tickCount={6}
                tick={<CustomRadiusTick />}
              />
              <Tooltip content={<CustomTooltip tests={tests} />} />
              {tests.map((test, index) =>
                visibleTests.has(test.id) ? (
                  <Radar
                    key={test.id}
                    name={test.name}
                    dataKey={test.id}
                    stroke={getTestColor(index)}
                    fill={getTestColor(index)}
                    fillOpacity={0.25}
                    strokeWidth={2}
                  />
                ) : null
              )}
              <Legend
                wrapperStyle={{ paddingTop: 20 }}
                formatter={(value) => (
                  <span className="text-sm text-foreground">{value}</span>
                )}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          Each axis represents a soil indicator rated on a 1-5 index scale.
          Higher values indicate better soil health for that indicator.
        </p>
      </CardContent>
    </Card>
  );
};

export default ComparisonRadarChart;
